import styled from '@emotion/styled';
import { useRef } from 'react';
import Button, { ButtonLabel } from '../../../components/common/button/Button';
import { Css, useIntersectionObserver, Responsive } from '../../../ui';
import PlaneIcon from '../../../assets/icons/flights/plane-up.svg';
import InformationIcon from '../../../assets/icons/info.svg';
import LocationIcon from '../../../assets/icons/location.svg';
import { zIndex } from '../../../ui/themer/theme/zIndex';
import { Shadow } from '../../../styles/Shadow';

interface ISearchFiltersProps {
	filterFlights: boolean;
	setFilterFlights: (filter: boolean) => void;
	filterLocations: boolean;
	setFilterLocations: (filter: boolean) => void;
	filterInformation: boolean;
	setFilterInformation: (filter: boolean) => void;
	scrollRef: React.RefObject<HTMLDivElement>;
}

export default function SearchFilters({
	filterFlights,
	setFilterFlights,
	filterLocations,
	setFilterLocations,
	filterInformation,
	setFilterInformation,
	scrollRef,
}: ISearchFiltersProps) {
	const ref = useRef<HTMLDivElement>(null);
	const isMobile = !!Responsive.useMatchesBreakpoint('mobileXL');
	const topMargin = isMobile ? '-16px' : '-24px';
	const intersect = useIntersectionObserver(ref, {
		threshold: 1,
		rootMargin: `${topMargin} 100% 100% 100%`,
		root: scrollRef.current,
	});
	const isSticky = !intersect?.isIntersecting;

	return (
		<Wrapper ref={ref}>
			<FilterButton
				text="Flights"
				icon={<PlaneIcon width={20} height={20} role="presentation" />}
				selected={filterFlights}
				isSticky={isSticky}
				onClick={() => setFilterFlights(!filterFlights)}
			/>
			<FilterButton
				text="Locations"
				icon={<LocationIcon width={20} height={20} role="presentation" />}
				selected={filterLocations}
				isSticky={isSticky}
				onClick={() => setFilterLocations(!filterLocations)}
			/>
			<FilterButton
				text="Information"
				icon={<InformationIcon width={20} height={20} role="presentation" />}
				selected={filterInformation}
				isSticky={isSticky}
				onClick={() => setFilterInformation(!filterInformation)}
			/>
		</Wrapper>
	);
}

const Wrapper = styled.div`
	z-index: ${zIndex.dropdown};
	position: sticky;
	top: ${Css.pxToRem(-8)};

	display: flex;
	gap: ${Css.pxToRem(8)};

	margin: 0 auto;
	max-width: 100%;

	padding: ${Css.pxToRem(1)} ${Css.pxToRem(16)} ${Css.pxToRem(32)}
		${Css.pxToRem(16)};

	scrollbar-width: none;

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		overflow-x: auto;
		&::-webkit-scrollbar {
			display: none;
		}
	}
`;

interface IFilterButtonProps {
	text: string;
	icon: React.ReactNode;
	selected: boolean;
	isSticky: boolean;
	onClick: () => void;
}

function FilterButton({
	text,
	icon,
	selected,
	isSticky,
	onClick,
}: IFilterButtonProps) {
	return (
		<StyledFilterButton
			buttonType="tag"
			role="switch"
			aria-checked={selected}
			inverted={!selected}
			iconPosition="left"
			icon={icon}
			isSticky={isSticky}
			onClick={onClick}
		>
			{text}
		</StyledFilterButton>
	);
}

const StyledFilterButton = styled(Button)<{
	isSticky: boolean;
}>`
	height: ${Css.pxToRem(48)};

	padding: ${Css.pxToRem(14)} ${Css.pxToRem(14)} ${Css.pxToRem(16)}
		${Css.pxToRem(14)};

	${ButtonLabel} {
		font-size: ${Css.pxToRem(16)};
		line-height: ${Css.pxToRem(24)};
	}

	${({ isSticky }) => isSticky && `box-shadow: ${Shadow.Desktop}`};
	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		height: ${Css.pxToRem(36)};

		padding: ${Css.pxToRem(8)} ${Css.pxToRem(12)} ${Css.pxToRem(8)}
			${Css.pxToRem(10)};

		${ButtonLabel} {
			font-size: ${Css.pxToRem(14)};
			line-height: ${Css.pxToRem(20)};
		}

		${({ isSticky }) => isSticky && `box-shadow: ${Shadow.Mobile}`};
	}
`;
